<template>
  <div>
    <StepHeader
      title="Wähle Datum & Uhrzeit"
      :currentStep="currentStep"
      :totalSteps="totalSteps"
    />
    <!-- Desktop -->
    <div class="hidden md:flex gap-6">
      <div class="w-72">
        <DatePickerPanel
            v-model="selectedDate"
            :calendarAttributes="calendarAttributes"
            :disabledDates="disabledDates"
            @dayclick="onAnyDayClicked"
        />
      </div>
      <TimeSlotList
        v-if="selectedDate"
        :timeSlots="availableTimeSlots"
        :selected="selectedTime"
        :formattedDate="formattedDate"
        @select="onTimeSlotSelect"
      />
    </div>

    <!-- Mobile -->
    <div class="block md:hidden w-full">
      <DatePickerPanel
        v-if="!showTimeSlotsMobile"
        v-model="selectedDate"
        :calendarAttributes="calendarAttributes"
        :disabledDates="disabledDates"
      />
      <div v-else class="mt-6">
        <h3 class="mb-3 font-semibold text-sm text-gray-800">
          {{ formattedDate }}
        </h3>
        <TimeSlotList
          :timeSlots="availableTimeSlots"
          :selected="selectedTime"
          @select="onTimeSlotSelect"
        />
      </div>
    </div>
  </div>
</template>


<script setup>
import DatePickerPanel from './DatePickerPanel.vue';
import TimeSlotList from './TimeSlotList.vue';
import StepHeader from '../StepHeader.vue';
import { ref, computed, watch } from 'vue'

const props = defineProps({
  meetingType: Object,
  availabilities: Object,
  currentStep: {
    type: Number,
    required: true
  },
  totalSteps: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(['next', 'back'])

const selectedDate = ref(null)
const selectedTime = ref(null)
const showTimeSlotsMobile = ref(false)

function formatDateKey(date) {
  if (!date) return ''
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
}

const availableTimeSlots = computed(() => {
  const key = formatDateKey(selectedDate.value)
  return props.availabilities[key] || []
})

const calendarAttributes = computed(() => {
  const today = new Date()
  return Array.from({ length: 90 }, (_, i) => {
    if (i === 0) return null

    const date = new Date(today)
    date.setDate(today.getDate() + i)

    const key = formatDateKey(date)

    if (props.availabilities[key]?.length > 0) {
      return {
        key: `available-${key}`,
        highlight: {
          fillMode: 'solid',
          style: { backgroundColor: '#E6F6FE' },
          borderRadius: '10px'
        },
        content: {
          color: 'green',
        },
        contentStyle: {
          padding: '8px',
          boxSizing: 'border-box'
        },
        dates: date
      }
    }
  }).filter(Boolean)
})

const disabledDates = computed(() => {
  const today = new Date()
  return Array.from({ length: 90 }, (_, i) => {
    const date = new Date(today)
    date.setDate(today.getDate() + i)
    const key = formatDateKey(date)
    if(i === 0) return date
    // Disable the date if it has no availabilities
    if (!props.availabilities[key] || props.availabilities[key].length === 0) {
      return date
    }
    return null
  }).filter(Boolean)
})

const formattedDate = computed(() => {
  if (!selectedDate.value) return ''
  return new Date(selectedDate.value).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long'
  })
})

function onAnyDayClicked(date) {
  const isDisabled = disabledDates.value.some(
    d => d.toDateString() === new Date(date).toDateString()
  )

  if (isDisabled) {
    // Reset selection if disabled
    selectedDate.value = null
    selectedTime.value = null
    showTimeSlotsMobile.value = false
  }
}

watch(selectedDate, (newDate) => {
  selectedTime.value = null

  if (newDate && window.innerWidth < 768) {
    showTimeSlotsMobile.value = true
  }
})

function onDateSelect(date) {
  selectedDate.value = date
}

function onTimeSlotSelect(time) {
  selectedTime.value = time
  submit()
}

function submit() {
  const date = new Date(selectedDate.value)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const formatted = `${yyyy}-${mm}-${dd}T${selectedTime.value}`

  emit('next', formatted)
}
</script>